import { useEffect, useState } from 'react';
import hubLayout from 'styles/css/hubLayout.module.css';
import Modal from "react-modal";
import styled from "styled-components";
import { IoIosArrowDown } from "react-icons/io";
import { getHappyCallTemplateAll } from 'api/api';
import { Events, HappyCallTemplate } from 'type/type';
import React from 'react';
import { PrimaryButton, PrimaryPinkButton } from './Button';

const CommonListWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  summary {
    min-height: 3rem;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 0.5rem 1rem;
    align-items: center;
    gap: 1rem;
    border-radius: 0.5rem;
    background-color: #777777;
    &:hover {
      background-color: #777777d1;
      cursor:pointer;
    }
    p:nth-of-type(2) {
      margin-left: auto;
    }
  }
  details[open] {
    summary {
      border-bottom-right-radius: 0;
      border-bottom-left-radius: 0;
    }
    nav {
      display: flex;
      white-space: break-spaces;
      padding: 1rem;
      min-height: 2.5rem;
      max-height: 35rem;
      overflow-y: auto;
      border-bottom-right-radius: 0.5rem;
      border-bottom-left-radius: 0.5rem;
      background-color: #e5e5e5;
      gap: 1rem;
      flex-wrap: wrap;
      &::-webkit-scrollbar {
        width: 10px !important; 
        height: 10px !important;  
      }
    
      div {
        color: #000;
        padding: 0.5rem;
        border: 1px solid #000;
        border-radius: 0.5rem;
        height: 15rem;
        width: 15rem;
        background: #cfdfed;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        span {
          height: 100%;
          overflow-y: auto;
          font-size: 13px;
          line-height: 1.5rem;
          
          &::-webkit-scrollbar {
            width: 10px !important; 
            height: 10px !important;  
          }
        }
      }
    }
  }
`;

const SelectMsg = styled.div`
  color: #000;
  padding: 1rem;
  border: 1px solid #000;
  border-radius: 0.5rem;
  background: #cfdfed;
  input {
    width: 10rem;
  }
  .btn-wrap {
    display: flex;
    gap: 10px;
    justify-content: center;
    margin-top: 3rem;
  }
`;

const ModalHeader = styled.div`
  height: 3rem;
  padding: 0.5rem;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  h2 {
    color: #000;
  }
  button {
    width: 2rem;
    height: 2rem;
  }
`;

type MessageWithInputsProps = {
  message: string;
  inputs?: Record<string, string>;
  setInputs?: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  event?: Events;
};

export const MessageWithInputs = ({ message, inputs, setInputs, event }: MessageWithInputsProps) => {
  const lines = message.replace(/\\n/g, '\n').split('\n');

  const renderLine = (line: string, lineIndex: number) => {
    const regex = /#\{(.*?)\}/g;
    const elements: React.ReactNode[] = [];

    if (inputs && setInputs) {
      // 🔹 치환 모드 (input 렌더링)
      let lastIndex = 0;
      let match;

      while ((match = regex.exec(line)) !== null) {
        const [placeholder, key] = match;
        const index = match.index;

        // 일반 텍스트
        if (lastIndex < index) {
          elements.push(line.slice(lastIndex, index));
        }

        // input field
        elements.push(
          <input
            key={`${key}-${lineIndex}-${index}`}
            type="text"
            placeholder={key}
            value={inputs[key] || ''}
            onChange={(e) =>
              setInputs((prev) => ({ ...prev, [key]: e.target.value }))
            }
            style={{ margin: '0 4px' }}
            required={true}
          />
        );

        lastIndex = index + placeholder.length;
      }

      // 남은 텍스트
      if (lastIndex < line.length) {
        elements.push(line.slice(lastIndex));
      }
    } else {
      // 🔹 일반 모드 (줄바꿈만 처리)
      elements.push(line);
    }

    return (
      <React.Fragment key={`line-${lineIndex}`}>
        {elements}
        <br />
      </React.Fragment>
    );
  };

  return <>{lines.map((line, idx) => renderLine(line, idx))}</>;
};

function HappyCallModal({ isOpen, setIsOpen, onConfirm }: { isOpen: boolean, setIsOpen:Function, onConfirm:Function }) {
  const [inputMsg, setInputMsg] = useState<Record<string, string>>({});
  const [happyCallTemplateArr, setHappyCallTemplateArr] = useState<Record<string, HappyCallTemplate[]>>({});
  const [selectTemplate, setSelectTemplate] = useState<HappyCallTemplate|null>(null);
  const customOrder = [
    "이벤트 알림",
    "기본",
    "전체시간대 기반",
    "잠금보안 이벤트 없음",
    "이벤트 발생",
    "외부 이벤트 없음",
    "내부 이벤트 없음",
  ];

  const choiceTemplate = (template: HappyCallTemplate) => {
    setInputMsg({});
    setSelectTemplate(template);
  }

  // 내부복사 Modal
  const closeModal = () => {
    setIsOpen(false); 
    if (window.history.state?.modalIsOpen) {
      window.history.back(); // 히스토리 상태 복구
    }
  }

  const getHappyCallTemplate = async () => {
    const happyCallTemplateRes = await getHappyCallTemplateAll(0, 0);
    if (happyCallTemplateRes.code === 200) {
      const grouped = groupByType(happyCallTemplateRes.data);
      setHappyCallTemplateArr(grouped);
    }
  };

  const groupByType = (data: HappyCallTemplate[]): Record<string, HappyCallTemplate[]> => {
    return data.reduce((acc: Record<string, HappyCallTemplate[]>, cur) => {
      if (!acc[cur.type]) {
        acc[cur.type] = [];
      }
      acc[cur.type].push(cur);
      return acc;
    }, {});
  };

  const handleConfirm = (target: string) => {
    if (!selectTemplate) {
      alert('다시 시도해 주세요.');
      return closeModal();
    }
  
    const hasVariables = /#\{.*?\}/.test(selectTemplate.message);
    console.log(Object.keys(inputMsg).length);
    const isAnyEmpty = hasVariables && (Object.keys(inputMsg).length === 0 || Object.values(inputMsg).some((v) => v.trim() === ''));
    if (isAnyEmpty && selectTemplate.code !== "event_detail") {
      return alert('모든 항목을 입력해주세요.');
    }
    onConfirm(target, inputMsg, selectTemplate);
  };

  // 뒤로가기 이벤트 처리
  useEffect(() => {
    const handlePopState = () => {
      if (isOpen) {
        closeModal();
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isOpen]);
  useEffect(() => {
    getHappyCallTemplate();
  }, []);
  return (
    <>
      <Modal
        isOpen={isOpen} // 모달 열림/닫힘 상태
        // onRequestClose={closeModal} // 모달 외부 클릭 시 닫힘
        ariaHideApp={false}
        style={{
          content: {
            width: '100%',
            maxWidth: '110rem',
            height: '70rem',
            top: "40%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            transform: "translate(-50%, -40%)",
          },
          overlay: {
            zIndex: "10000",
            backgroundColor: "rgba(0,0,0,.7)",
          },
        }}
      >
        <ModalHeader>
          <h2>{selectTemplate ? '해피콜 구문 편집' : '해피콜 템플릿'}</h2>
          <button type="button" onClick={closeModal}>X</button>
        </ModalHeader>
        <CommonListWrap>
          {selectTemplate ? (
            // 선택된 템플릿이 있을 경우 보여줄 메시지
            <>
              <SelectMsg>
                <MessageWithInputs message={selectTemplate.message} inputs={selectTemplate.code === "event_detail" ? undefined : inputMsg} setInputs={selectTemplate.code === "event_detail" ? undefined : setInputMsg} />
                <div className='btn-wrap'>
                  <PrimaryPinkButton type="button" onClick={() => setSelectTemplate(null)}>뒤로가기</PrimaryPinkButton>
                  <PrimaryButton type="button" onClick={() => handleConfirm("고객")}>고객 전송</PrimaryButton>
                  {/* <PrimaryButton type="button" onClick={() => handleConfirm("119")}>119 전송</PrimaryButton>
                  <PrimaryButton type="button" onClick={() => handleConfirm("112")}>112 전송</PrimaryButton> */}
                </div>
              </SelectMsg>
            </>
          ) : (
            customOrder.filter((type) => happyCallTemplateArr[type]).map((type, idx) => (
              <details key={`${type}_${idx}`} open={type === "이벤트 알림"}>
                <summary style={{ display: 'flex', justifyContent: 'space-between', cursor: 'pointer' }}>
                  <p>{type}</p>
                  <p><IoIosArrowDown /></p>
                </summary>
                <nav>
                  {happyCallTemplateArr[type].map((item) => (
                    <div
                      key={item.code}
                      style={{ display: 'flex', justifyContent: 'space-between', margin: '5px 0', cursor: 'pointer' }}
                      onClick={() => choiceTemplate(item)}
                    >
                      <span>
                        <p>
                          {item.message.replace(/\\n/g, '\n').split('\n').map((line, index) => (
                            <React.Fragment key={`message_${index}`}>
                              {line}
                              <br />
                            </React.Fragment>
                          ))}
                        </p>
                      </span>
                    </div>
                  ))}
                </nav>
              </details>
            ))
          )}
        </CommonListWrap>
      </Modal>
    </>
  );
}

export default HappyCallModal;